import { Placement } from "@blueprintjs/core";
import React from "react";

import { HexMenuItem } from "../../../hex-components/HexMenuItem";
import CollectionsGateIcon from "../../../static/illustration/illustration-featuregate-collections.inline.svg";
import ComponentFeatureGateIcon from "../../../static/illustration/illustration-featuregate-components.inline.svg";
import { TestSelectors } from "../../../util/testSelectors";
import { FeatureGateToolTip } from "../../feature-gate/FeatureGateToolTip";
import {
  CollectionIcon,
  ComponentIcon,
  ExploreIcon,
  ProjectIcon,
  RLanguageIcon,
  ThreadIcon,
  UploadIcon,
} from "../../icons/CustomIcons";

import {
  CreateMenuLocation,
  useCreateCollectionButton,
  useCreateComponentButton,
  useCreateExplorationButton,
  useCreateProjectButton,
  useCreateThreadButton,
} from "./useCreateAssets";

export const CreateThreadMenuItem = React.memo(function CreateThreadMenuItem({
  location,
}: {
  location: CreateMenuLocation;
}) {
  const { canCreateNewThread, createNewThread } = useCreateThreadButton({
    location,
  });
  return (
    <HexMenuItem
      disabled={!canCreateNewThread}
      icon={<ThreadIcon />}
      text="New thread"
      onClick={createNewThread}
    />
  );
});

export const CreateProjectMenuItem = React.memo(function CreateProjectMenuItem({
  location,
}: {
  location: CreateMenuLocation;
}) {
  const { createNewProject, newProjectDisabled } = useCreateProjectButton({
    location,
  });
  return (
    <HexMenuItem
      data-test={TestSelectors.CREATE_PROJECT}
      disabled={newProjectDisabled}
      icon={<ProjectIcon />}
      text="New project"
      onClick={createNewProject}
    />
  );
});

export const CreateComponentMenuItem = React.memo(
  function CreateComponentMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      componentsFeatureGate,
      createPythonComponent,
      newComponentDisabled,
    } = useCreateComponentButton({
      location,
    });
    return (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsFeatureGate}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexMenuItem
          data-test={TestSelectors.CREATE_COMPONENT}
          disabled={newComponentDisabled}
          icon={<ComponentIcon />}
          text="New component"
          onClick={createPythonComponent}
        />
      </FeatureGateToolTip>
    );
  },
);

export const CreateRProjectMenuItem = React.memo(
  function CreateRProjectMenuItem({
    location,
  }: {
    location: CreateMenuLocation;
  }) {
    const { createRProject, newProjectDisabled } = useCreateProjectButton({
      location,
    });
    return (
      <HexMenuItem
        data-test={TestSelectors.CREATE_R_PROJECT}
        disabled={newProjectDisabled}
        icon={<RLanguageIcon />}
        text="New R project"
        onClick={createRProject}
      />
    );
  },
);

export const CreateRComponentMenuItem = React.memo(
  function CreateRComponentMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const { componentsFeatureGate, createRComponent, newComponentDisabled } =
      useCreateComponentButton({
        location,
      });
    return (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsFeatureGate}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexMenuItem
          data-test={TestSelectors.CREATE_R_COMPONENT}
          disabled={newComponentDisabled}
          icon={<RLanguageIcon />}
          text="New R component"
          onClick={createRComponent}
        />
      </FeatureGateToolTip>
    );
  },
);

export const ImportProjectMenuItem = React.memo(function ImportProjectMenuItem({
  location,
}: {
  location: CreateMenuLocation;
}) {
  const { newProjectDisabled, openImportDialog } = useCreateProjectButton({
    location,
  });
  return (
    <HexMenuItem
      data-test={TestSelectors.IMPORT_PROJECT}
      disabled={newProjectDisabled}
      icon={<UploadIcon />}
      text="Upload project"
      onClick={openImportDialog}
    />
  );
});

export const ImportComponentMenuItem = React.memo(
  function ImportComponentMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      componentsFeatureGate,
      newComponentDisabled,
      openComponentImportDialog,
    } = useCreateComponentButton({
      location,
    });
    return (
      <FeatureGateToolTip
        descriptionOverride="Components give you the flexibility to reuse logic across multiple projects from a single source. Upgrade your plan to use components."
        disabled={componentsFeatureGate}
        featureGate="components"
        icon={<ComponentFeatureGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable components"
      >
        <HexMenuItem
          disabled={newComponentDisabled}
          icon={<UploadIcon />}
          text="Upload component"
          onClick={openComponentImportDialog}
        />
      </FeatureGateToolTip>
    );
  },
);

export const CreateCollectionMenuItem = React.memo(
  function CreateCollectionMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      collectionsFeatureGate,
      newCollectionDisabled,
      openNewCollectionDialog,
    } = useCreateCollectionButton({
      location,
    });
    return (
      <FeatureGateToolTip
        descriptionOverride="Collections give you the ability to organize and share your projects."
        disabled={collectionsFeatureGate}
        featureGate="collections"
        icon={<CollectionsGateIcon />}
        placement={placement}
        titleOverride="Upgrade plan to enable collections"
      >
        <HexMenuItem
          disabled={newCollectionDisabled}
          icon={<CollectionIcon />}
          text="New collection"
          onClick={openNewCollectionDialog}
        />
      </FeatureGateToolTip>
    );
  },
);

export const CreateExplorationMenuItem = React.memo(
  function CreateExplorationMenuItem({
    location,
    placement,
  }: {
    location: CreateMenuLocation;
    placement?: Placement;
  }) {
    const {
      createNewExplore,
      exploreFromScratchFeatureGate,
      newExplorationDisabled,
    } = useCreateExplorationButton({
      location,
    });
    return (
      <FeatureGateToolTip
        action="explore outside of a published app"
        disabled={exploreFromScratchFeatureGate}
        featureGate="exploreFromScratch"
        placement={placement}
      >
        <HexMenuItem
          data-test={TestSelectors.NEW_EXPLORATION}
          disabled={newExplorationDisabled}
          icon={<ExploreIcon />}
          text="New exploration"
          onClick={createNewExplore}
        />
      </FeatureGateToolTip>
    );
  },
);
